import { ReactElement, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router'

import {
  ACCOUNT_CLOSED,
  ACCOUNT_CLOSED_WITH_BALANCE,
  ACCOUNT_CREDIT_LINE_INCREASE_ELIGIBLE,
  ACCOUNT_DELINQUENT
} from 'const/account/conditions'
import { PSCC, RC } from 'const/account/products'
import featureFlags from 'const/featureFlags'
import { FAR_MESSAGE_PEN } from 'const/icons'
import routes from 'const/routes'
import { useAccountFeatures, useAccountSummary } from 'const/syfApiQueries'
import useNavigateOnRedirect from 'hooks/useNavigateOnRedirect'
import { useIVASetChatStatus } from 'iva-react-web-ui'
import i18n from 'strings/i18n'
import { VerticalCardLoader } from 'syf-component-library/ui/atoms'
import triggerAnalyticsEvent, {
  analyticsConfig
} from 'syf-js-utilities/helpers/triggerAnalyticsEvent'
import { IVALaunchPointName } from 'typings/custom'

import {
  cardStatusTrackerModalContainerId,
  changePaymentDueDateModalContainerId,
  creditLimitIncreaseContainerId,
  manageCardHoldersModalContainerId,
  paymentSecurityModalContainerId,
  replaceYourCardModalContainerId
} from 'const/modalContainerId'
import { LoaderContainer, MFEFullPageWrapper } from 'pages/subcomponents'
import { useAtomValue, useSetAtom } from 'jotai'
import { modalStatusAtom, selectedAccountAtom } from 'store/atoms'
import {
  AccountServiceContainer,
  Header,
  Icon,
  LinkButton,
  LinkContainer,
  LinkHeader,
  LinkItem,
  LinkList
} from './subcomponents'

const accountServiceAnalyticText = 'account services'

const AccountServices = (): ReactElement => {
  const updateModalStatus = useSetAtom(modalStatusAtom)
  const setSelectedAccount = useSetAtom(selectedAccountAtom)
  const { pointOfNeedChat } = useIVASetChatStatus()
  const navigate = useNavigate()

  const selectedAccount = useAtomValue(selectedAccountAtom)

  const {
    data: { conditions = [], product, servicing } = {},
    isLoading: isAccountSummaryLoading,
    isError: isAccountSummaryError
  } = useAccountSummary(selectedAccount)

  const { data: accountFeatures } = useAccountFeatures(selectedAccount)
  const accessToManageAuthUser = accountFeatures?.accessToManageAuthUser
  const accessToPaymentSecurity = accountFeatures?.accessToPaymentSecurity

  const isPaymentSecurityEnabled =
    featureFlags.isPaymentSecurityEnabled && accessToPaymentSecurity
  const accessToCreditView = accountFeatures?.accessToCreditView || false
  const isSamsClientId = product?.clientId === 'sams'

  useNavigateOnRedirect()

  useEffect(() => {
    if (!product?.type || !product?.clientId) return
    analyticsConfig.defaults.Product = product?.type
    analyticsConfig.defaults.ClientName = product?.clientId

    triggerAnalyticsEvent({
      PageFunction: accountServiceAnalyticText,
      PageKind: accountServiceAnalyticText,
      pageName: 'view'
    })
  }, [product?.type, product?.clientId])

  if (isAccountSummaryError) {
    return <Navigate to={routes.ERROR} />
  }

  const isAccountCreditLimitIncreaseEnabled =
    conditions?.includes(ACCOUNT_CREDIT_LINE_INCREASE_ELIGIBLE) &&
    accountFeatures?.accessToCreditLimitIncrease

  const isDelinquent = conditions?.includes(ACCOUNT_DELINQUENT)

  const isRevolving = [RC, PSCC].includes(product?.lineOfBusiness)

  const isCreditViewDashboardEnabled =
    featureFlags.isCreditViewDashboardEnabled && accessToCreditView

  const handleIVALaunch = (launchPON: IVALaunchPointName) => {
    pointOfNeedChat(launchPON, selectedAccount)
  }

  const isAccountClosed =
    conditions?.includes(ACCOUNT_CLOSED) ||
    conditions?.includes(ACCOUNT_CLOSED_WITH_BALANCE)

  const isManageCardholdersEnabled =
    featureFlags.isManageCardHolderEnabled &&
    accessToManageAuthUser &&
    servicing?.allowable &&
    !isAccountClosed

  const isManageCardholdersSamsEnabled =
    featureFlags.isManageCardHolderSamsEnabled &&
    isSamsClientId &&
    servicing?.allowable &&
    !isAccountClosed

  const showManageCardholders =
    isManageCardholdersEnabled || isManageCardholdersSamsEnabled
  const showChangePaymentDueDate = accountFeatures?.accessToChangeDueDate

  const creditLimitIncreaseButtonId =
    creditLimitIncreaseContainerId(selectedAccount)
  const manageCardholdersButtonId =
    manageCardHoldersModalContainerId(selectedAccount)
  const changePaymentDueDateButtonId =
    changePaymentDueDateModalContainerId(selectedAccount)
  const replaceYourCardButtonId =
    replaceYourCardModalContainerId(selectedAccount)
  const paymentSecurityButtonId =
    paymentSecurityModalContainerId(selectedAccount)

  const cardStatusTrackerButtonId =
    cardStatusTrackerModalContainerId(selectedAccount)

  const handleManageCardHolder = () => {
    setSelectedAccount(selectedAccount)
    updateModalStatus('ManageCardholders', manageCardholdersButtonId)
  }

  const handleCreditLimitIncreaseClick = () => {
    if (featureFlags.isCreditLimitIncreaseEnabled) {
      setSelectedAccount(selectedAccount)
      updateModalStatus('CreditLimitIncrease', creditLimitIncreaseButtonId)
    } else {
      handleIVALaunch('CLI')
    }
  }

  const handleCreditView = () => {
    navigate(routes.CREDIT_VIEW)
  }

  const handleChangePaymentDueDate = () => {
    if (featureFlags.isChangePaymentDueDateEnabled) {
      setSelectedAccount(selectedAccount)
      updateModalStatus('ChangePaymentDueDate', changePaymentDueDateButtonId)
    } else {
      handleIVALaunch('CPDD')
    }
  }

  const handleReplaceYourCard = () => {
    if (featureFlags.isReplaceYourCardEnabled) {
      setSelectedAccount(selectedAccount)
      updateModalStatus('ReplaceYourCard', replaceYourCardButtonId)
    } else {
      handleIVALaunch('RC')
    }
  }

  const handleCardStatusTracker = () => {
    if (featureFlags.isCardStatusTrackerEnabled) {
      setSelectedAccount(selectedAccount)
      updateModalStatus('CardStatusTracker', cardStatusTrackerButtonId)
    }
  }

  const handlePaymentSecurity = () => {
    if (isPaymentSecurityEnabled) {
      setSelectedAccount(selectedAccount)
      updateModalStatus('PaymentSecurity', paymentSecurityButtonId)
    }
  }

  return (
    <MFEFullPageWrapper>
      {isAccountSummaryLoading ? (
        <LoaderContainer>
          <VerticalCardLoader />
        </LoaderContainer>
      ) : (
        <>
          <AccountServiceContainer>
            <Header weight="bold" data-test="account-services">
              {i18n({
                labels: 'accountsServices'
              })}
            </Header>
          </AccountServiceContainer>
          <LinkContainer>
            <LinkItem>
              <LinkHeader weight="bold">
                {i18n({
                  labels: 'accountActions'
                })}
              </LinkHeader>
              <LinkList>
                {showChangePaymentDueDate && (
                  <div id={changePaymentDueDateButtonId}>
                    <LinkButton
                      buttonType="text"
                      data-analytics="clicktrackingevent"
                      data-object="link"
                      data-reason="change payment due date"
                      data-test="change-payment-due-date"
                      data-type={accountServiceAnalyticText}
                      onClick={handleChangePaymentDueDate}
                    >
                      {!featureFlags.isChangePaymentDueDateEnabled && (
                        <Icon icon={FAR_MESSAGE_PEN} />
                      )}
                      {i18n({
                        button: 'changePaymentDueDate'
                      })}
                    </LinkButton>
                  </div>
                )}
                {isAccountCreditLimitIncreaseEnabled &&
                  isRevolving &&
                  !isDelinquent && (
                    <div id={creditLimitIncreaseButtonId}>
                      <LinkButton
                        buttonType="text"
                        data-analytics="clicktrackingevent"
                        data-object="button"
                        data-reason="credit limit increase"
                        data-test="credit-limit-increase"
                        data-type={accountServiceAnalyticText}
                        dataCy="credit-limit-increase"
                        onClick={handleCreditLimitIncreaseClick}
                      >
                        {!featureFlags.isCreditLimitIncreaseEnabled && (
                          <Icon icon={FAR_MESSAGE_PEN} />
                        )}
                        {i18n({
                          button: 'creditLimitIncrease'
                        })}
                      </LinkButton>
                    </div>
                  )}
                {showManageCardholders && (
                  <div id={manageCardholdersButtonId}>
                    <LinkButton
                      buttonType="text"
                      data-analytics="clicktrackingevent"
                      data-object="button"
                      data-reason="manage cardholders"
                      data-test="manage-cardholders"
                      data-type={accountServiceAnalyticText}
                      onClick={handleManageCardHolder}
                    >
                      {i18n({
                        button: 'manageCardholders'
                      })}
                    </LinkButton>
                  </div>
                )}
                {isCreditViewDashboardEnabled && (
                  <LinkButton
                    buttonType="text"
                    data-type={accountServiceAnalyticText}
                    data-reason="creditview dashboard"
                    data-object="button"
                    data-analytics="clicktrackingevent"
                    data-test="credit-view-dashboard"
                    onClick={handleCreditView}
                  >
                    {i18n({
                      button: 'creditViewDashboard'
                    })}
                  </LinkButton>
                )}
                {isPaymentSecurityEnabled && (
                  <div id={paymentSecurityButtonId}>
                    <LinkButton
                      buttonType="text"
                      data-analytics="clicktrackingevent"
                      data-object="link"
                      data-reason="payment security"
                      data-test="payment-security"
                      data-type={accountServiceAnalyticText}
                      onClick={handlePaymentSecurity}
                    >
                      {i18n({
                        button: 'paymentSecurity'
                      })}
                    </LinkButton>
                  </div>
                )}
              </LinkList>
            </LinkItem>
            <LinkItem>
              <LinkHeader weight="bold">
                {i18n({
                  labels: 'cardActions'
                })}
              </LinkHeader>

              <LinkList>
                {isRevolving && !isDelinquent && (
                  <>
                    <LinkButton
                      buttonType="text"
                      dataCy="activate-a-card"
                      data-type={accountServiceAnalyticText}
                      data-reason="activate a card"
                      data-object="link"
                      data-analytics="clicktrackingevent"
                      data-test="activate-a-card"
                      onClick={() => handleIVALaunch('AC')}
                    >
                      <Icon icon={FAR_MESSAGE_PEN} />
                      {i18n({
                        button: 'activateACard'
                      })}
                    </LinkButton>
                    <LinkButton
                      buttonType="text"
                      dataCy="freeze-card"
                      data-type={accountServiceAnalyticText}
                      data-reason="freeze card"
                      data-object="link"
                      data-analytics="clicktrackingevent"
                      data-test="freeze-card"
                      onClick={() => handleIVALaunch('FC')}
                    >
                      <Icon icon={FAR_MESSAGE_PEN} />
                      {i18n({
                        button: 'freezeCard'
                      })}
                    </LinkButton>
                  </>
                )}

                {(isDelinquent || isRevolving) && (
                  <LinkButton
                    buttonType="text"
                    data-analytics="clicktrackingevent"
                    data-object="link"
                    data-reason="replace your card"
                    data-test="replace-your-card"
                    data-type={accountServiceAnalyticText}
                    onClick={handleReplaceYourCard}
                  >
                    {!featureFlags.isReplaceYourCardEnabled && (
                      <Icon icon={FAR_MESSAGE_PEN} />
                    )}
                    {i18n({
                      button: 'replaceYourCard'
                    })}
                  </LinkButton>
                )}
                {featureFlags.isCardStatusTrackerEnabled && (
                  <LinkButton
                    id="card-status-tracker-link"
                    buttonType="text"
                    data-analytics="clicktrackingevent"
                    data-object="link"
                    data-reason="track your card"
                    data-test="track-your-card"
                    data-type={accountServiceAnalyticText}
                    onClick={handleCardStatusTracker}
                  >
                    {i18n({
                      button: 'trackYourCard'
                    })}
                  </LinkButton>
                )}
              </LinkList>
            </LinkItem>
            <LinkItem>
              <LinkHeader weight="bold">
                {i18n({
                  labels: 'security'
                })}
              </LinkHeader>
              <LinkList>
                {(isDelinquent || isRevolving) && (
                  <LinkButton
                    buttonType="text"
                    data-type={accountServiceAnalyticText}
                    data-reason="report lost or stolen card"
                    data-object="link"
                    data-analytics="clicktrackingevent"
                    data-test="report-lost-stolen-card"
                    onClick={() => handleIVALaunch('RLS')}
                  >
                    <Icon icon={FAR_MESSAGE_PEN} />
                    {i18n({
                      button: 'reportLostStolenCard'
                    })}
                  </LinkButton>
                )}

                {isRevolving && !isDelinquent && (
                  <LinkButton
                    buttonType="text"
                    dataCy="create-change-pin"
                    data-type={accountServiceAnalyticText}
                    data-reason="create/change PIN"
                    data-object="link"
                    data-analytics="clicktrackingevent"
                    data-test="create-change-pin"
                    onClick={() => handleIVALaunch('CCP')}
                  >
                    <Icon icon={FAR_MESSAGE_PEN} />
                    {i18n({
                      button: 'createChangePIN'
                    })}
                  </LinkButton>
                )}
              </LinkList>
            </LinkItem>
          </LinkContainer>
        </>
      )}
    </MFEFullPageWrapper>
  )
}

export default AccountServices
